import { Dispatch } from '@reduxjs/toolkit';
import { ALL, AM_DISCLAIMER, EU_DISCLAIMER, EVP_SET, IPU_AM, IPU_EU } from '../api/constants';
import { five, four, naturalGas, one, pipeLineGas, PLANNING_ONE_SOURCE, power, six, ten, two, zero } from '../constants';
import { CommodityTableRow, ICommodityType, ITradeData, ITransformDataObject, PlannedVsCommitted } from '../Types/types';
import { HighchartsReactProps } from 'highcharts-react-official';
import { replaceEvpOption } from '../components/PortfolioDetails/utils';
export const showSpinner = (dispatch: Dispatch) => {
  dispatch({ type: 'setSpinner', payload: true });
};

export const hideSpinner = (dispatch: Dispatch) => {
  dispatch({ type: 'setSpinner', payload: false });
};

export const currentYear = new Date().getFullYear();

export const getSummedUpLeData = (
  manualLeValue: Record<string, ICommodityType>,
  isToggle: boolean,
  commodity: string,
) => {
  const getValue = (type: 'Volume' | 'Emission', commodityName: string) =>
    manualLeValue[commodityName]?.[type] || 0;
  const emissionOrVolume = isToggle ? 'Volume' : 'Emission';
  if (commodity === ALL) {
    const powerValue = getValue(emissionOrVolume, power);
    const pipeLineGasValue = getValue(emissionOrVolume, pipeLineGas);
    return powerValue + pipeLineGasValue;
  } else {
    return getValue(emissionOrVolume, commodity);
  }
};

export const getDisclaimer = (evpVal: string, seIpuRegionVal: string): string[] => {
  if (evpVal?.includes(EVP_SET) && seIpuRegionVal?.includes(IPU_EU)) {
    return [EU_DISCLAIMER];
  } else if (evpVal?.includes(EVP_SET) && seIpuRegionVal?.includes(IPU_AM)) {
    return [AM_DISCLAIMER];
  } else {
    return [];
  }
};

const createDeltaSeries = (name: string, color: string, dataValue: number, leValue: number) => {
  const dataArray = Array.from({ length: 10 }, () => null) as unknown as number[];
  dataArray[0] = Math.abs(leValue - dataValue);
  return {
    type: 'column',
    name,
    color,
    dashStyle: 'Solid',
    data: dataArray,
  };
};

export const getDeltaLeAndCommodity = (
  data: ITransformDataObject[],
  manualLeValue: Record<string, ICommodityType>,
  isToggle: boolean,
  selectedCommodity: string,
) => {
  const removeDealsData = data.filter((item) => !item.name.includes('Deal'));
  const getLeData = (commodity: string) =>
    isToggle ? (manualLeValue[commodity]?.Volume || 0) : (manualLeValue[commodity]?.Emission || 0);
  const getDataValue = (commodity: string) =>
    data.find((item) => item.name === commodity)?.data[0] ?? 0;
  if (selectedCommodity === ALL) {
    const powerData = getDataValue(power);
    const pipelineGasData = getDataValue(pipeLineGas);
    const powerLeValue = getLeData(power);
    const pipeLineGasValue = getLeData(pipeLineGas);
    const deltaPower = createDeltaSeries(
      'Gap to LE (Power)',
      '#6E94C0',
      powerData,
      powerLeValue,
    );
    const deltaPipelineGas = createDeltaSeries(
      'Gap to LE (Pipeline Gas)',
      '#FFF7B4',
      pipelineGasData,
      pipeLineGasValue,
    );
    let allData = [...removeDealsData];
    if (powerLeValue > 0) {
      allData = [...allData, deltaPower];
    }
    if (pipeLineGasValue > 0) {
      allData = [...allData, deltaPipelineGas];
    }
    return allData;
  } else {
    const commodityLeData = getSummedUpLeData(manualLeValue, isToggle, selectedCommodity);
    const commodityData = getDataValue(selectedCommodity);
    const color = selectedCommodity === pipeLineGas ? '#FFF7B4' : '#6E94C0';
    const deltaCommodity = createDeltaSeries(
      `Gap to LE (${selectedCommodity})`,
      color,
      commodityData,
      commodityLeData,
    );
    return [...removeDealsData, deltaCommodity];
  }
};

export const createLatestEstimateSeries = (commodity: CommodityTableRow, color: string) => {
  return {
    type: 'column', // Change this to the desired chart type
    name: `${commodity.TRADE_COMMODITY_NAME}`,
    data: [
      {
        y: commodity.latest_estimate,
        x: four,
        customColor: 'blue', // Optional: Add custom properties if needed
      },
    ],
    color,
    showInLegend: false,
    dataLabels: {
      useHTML: true,
      enabled: true,
      style: {
        fontSize: '0.5px',
        fontWeight: 'normal',
        textShadow: 'none',
        color: 'rgb(255, 255, 255)',
        textOutline: 'none',
      },
      formatter() {
        const currentPoint = (this as HighchartsReactProps)?.point;
        const { customText, customColor } = currentPoint ?? {};
        if (!customText || !customColor) {
          return '';
        }

        const category = (this as HighchartsReactProps)?.x;
        if (category === '') {
          return `<span style="color: ${customColor}">${customText}</span>`;
        }
        return '';
      },
    },
    pointPadding: 0,
  };
};

export const createMainSeries = (commodity: CommodityTableRow, color: string, index: number) => {
  const data = Array(five).fill(null);
  data[0] = commodity.plan;
  data[index] = {
    y: commodity.plan - commodity.latest_estimate,
    customColor: commodity.plan > commodity.latest_estimate ? 'green' : 'red',
  };

  return {
    name: commodity.TRADE_COMMODITY_NAME,
    data,
    color,
    dataLabels: {
      useHTML: true,
      enabled: true,
      style: {
        fontSize: '0.5px',
        fontWeight: 'normal',
        textShadow: 'none',
        color: 'rgb(255, 255, 255)',
        textOutline: 'none',
      },
      formatter() {
        const currentPoint = (this as HighchartsReactProps)?.point;
        const { customText, customColor } = currentPoint ?? {};
        if (!customText || !customColor) {
          return '';
        }

        const category = (this as HighchartsReactProps)?.x;
        if (category === '') {
          return `<span style="color: ${customColor}">${customText}</span>`;
        }
        return '';
      },
    },
    pointPadding: 0,
  };
};

export const updateCommidityFilterOption = (label: string, item: string) => {
  if (label === 'IPU Region') {
    return item.replace('SE', '');
  } else {
    return item === pipeLineGas ? item.replace(item, 'Gas') : replaceEvpOption(item);
  }
};

export const transformedPlanData = (planData: PlannedVsCommitted[]) => {
  const summedCarbonEmissions = planData.reduce((acc, curr) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const { TRADE_YEAR, CARBON_EMISSION, QUANTITY, EVP, ...rest } = curr;
    if (TRADE_YEAR in acc) {
      acc[TRADE_YEAR].CARBON_EMISSION += CARBON_EMISSION;
      acc[TRADE_YEAR].QUANTITY += QUANTITY;
    } else {
      acc[TRADE_YEAR] = { TRADE_YEAR, CARBON_EMISSION, QUANTITY, ...rest };
    }
    return acc;
  }, {} as { [key: number]: Omit<PlannedVsCommitted, 'EVP'> });
  return Object.values(summedCarbonEmissions);
};
export function roundNumberStringToDecimalPlaces(
  value: string | undefined | null,
  decimalPlaces: number,
): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  const parsedValue = parseFloat(value);

  if (isNaN(parsedValue)) {
    return value; // Return the original value if it's not a valid number
  }

  const roundedValue =
    Math.round(parsedValue * Math.pow(ten, decimalPlaces)) / Math.pow(ten, decimalPlaces);
  return roundedValue.toFixed(decimalPlaces);
}

export function roundNumberStringToNoDecimal(value: string | undefined | null): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  const parsedValue = parseFloat(value);

  if (isNaN(parsedValue)) {
    return value; // Return the original value if it's not a valid number
  }

  const roundedValue = Math.round(parsedValue);
  return roundedValue.toString();
}

export function roundNumber(num: number, decimalPlaces: number): number {
  const multiplier = Math.pow(ten, decimalPlaces);
  return Math.round(num * multiplier) / multiplier;
}
export function commaSeparatedKPI(uniqueKPIs: string[]): string {
  let commaSeparatedString = '';

  if (uniqueKPIs.length === one) {
    commaSeparatedString = uniqueKPIs[zero];
  } else if (uniqueKPIs.length > one) {
    for (let i = zero; i < uniqueKPIs.length - one; i++) {
      commaSeparatedString += uniqueKPIs[i] + ', ';
    }
    commaSeparatedString += 'and ' + uniqueKPIs[uniqueKPIs.length - 1];
  }

  return commaSeparatedString;
}

export function carbonColor(value: number): string {
  const nineFive = 95;
  const nineNine = 99;
  if (value < nineFive) {
    return '#008000'; // Green color
  } else if (value >= nineFive && value <= nineNine) {
    return '#FFBF00'; // Yellow color
  } else {
    return '#ff0000'; // Red
  }
}
const oneSix = 16;
export function hexToRgb(hexCode: string) {
  // Remove the "#" symbol (if present)
  const hex = hexCode.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(hex.substring(zero, two), oneSix);
  const g = parseInt(hex.substring(two, four), oneSix);
  const b = parseInt(hex.substring(four, six), oneSix);

  return { r, g, b };
}
export function lightenColor(hexCode: string, percent: number) {
  const { r, g, b } = hexToRgb(hexCode);

  // Apply percentage of white to lighten the color
  const twoDoubleFive = 255;
  const oneDoubleZero = 100;
  const rLightened = Math.min(twoDoubleFive, r + ((twoDoubleFive - r) * percent) / oneDoubleZero);
  const gLightened = Math.min(twoDoubleFive, g + ((twoDoubleFive - g) * percent) / oneDoubleZero);
  const bLightened = Math.min(twoDoubleFive, b + ((twoDoubleFive - b) * percent) / oneDoubleZero);

  // Convert the lightened RGB values back to hex
  const rHexNumber = Math.round(rLightened).toString(oneSix).padStart(two, '0');
  const gHexNumber = Math.round(gLightened).toString(oneSix).padStart(two, '0');
  const bHexNumber = Math.round(bLightened).toString(oneSix).padStart(two, '0');
  return `#${rHexNumber}${gHexNumber}${bHexNumber}`;
}

// Calculation for Emission By Trade Commodity
export const calculateEmissionsByTradeCommodityName = (
  tradedata: ITradeData[],
): {
  TRADE_COMMODITY_NAME: string;
  plan: number;
  delta: number;
  latest_estimate: number;
  source: string;
}[] => {
  const emissionsByTradeCommodityName: {
    [tradeCommodityName: string]: { plan: number; latest_estimate: number; source: string };
  } = {};

  for (const item of tradedata) {
    const { TRADE_COMMODITY_NAME, SOURCE, CARBON_EMISSION } = item;

    if (!(TRADE_COMMODITY_NAME in emissionsByTradeCommodityName)) {
      emissionsByTradeCommodityName[TRADE_COMMODITY_NAME] = {
        plan: 0,
        latest_estimate: 0,
        source: '',
      };
    }

    if (SOURCE === PLANNING_ONE_SOURCE) {
      emissionsByTradeCommodityName[TRADE_COMMODITY_NAME].plan += CARBON_EMISSION;
    } else {
      emissionsByTradeCommodityName[TRADE_COMMODITY_NAME].latest_estimate += CARBON_EMISSION;
    }
    emissionsByTradeCommodityName[TRADE_COMMODITY_NAME].source = SOURCE;
  }
  const resultArray: {
    TRADE_COMMODITY_NAME: string;
    plan: number;
    delta: number;
    latest_estimate: number;
    source: string;
  }[] = [];
  for (const tradeCommodityName in emissionsByTradeCommodityName) {
    if (tradeCommodityName in emissionsByTradeCommodityName) {
      const { plan, latest_estimate, source } = emissionsByTradeCommodityName[tradeCommodityName];
      const changedTradeCommodityName =
        tradeCommodityName === naturalGas ? pipeLineGas : tradeCommodityName;
      const delta = plan - latest_estimate;
      resultArray.push({
        TRADE_COMMODITY_NAME: changedTradeCommodityName,
        plan,
        delta,
        latest_estimate,
        source,
      });
    }
  }
  return resultArray;
};